import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Project } from "@/features/projects/projectTypes";

type ProjectListProps = {
  projects: Project[];
  editingProjectId: string | null;
  onStartEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  renderEditForm: (project: Project) => ReactNode;
};

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function formatStatus(status: Project["status"]): string {
  return status.replace("_", " ");
}

export function ProjectList({
  projects,
  editingProjectId,
  onStartEdit,
  onDelete,
  renderEditForm,
}: ProjectListProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <ul className="divide-y divide-slate-200">
        {projects.map((project) => (
          <li key={project.id} className="p-4 sm:p-5">
            {editingProjectId === project.id ? (
              renderEditForm(project)
            ) : (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h3 className="break-words text-base font-semibold">
                    <Link
                      to={`/projects/${project.id}`}
                      className="text-slate-950 hover:text-indigo-700 hover:underline focus:text-indigo-700 focus:underline focus:outline-none"
                    >
                      {project.name}
                    </Link>
                  </h3>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                    <span className="rounded bg-slate-100 px-2 py-1 capitalize text-slate-700">
                      {formatStatus(project.status)}
                    </span>
                    <span>Updated {formatDate(project.updated_at)}</span>
                    <span>Created {formatDate(project.created_at)}</span>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Link
                    to={`/projects/${project.id}`}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Open
                  </Link>
                  <button
                    type="button"
                    onClick={() => onStartEdit(project)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(project)}
                    className="rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
